import {
	ChangeDetectionStrategy,
	Component,
	Input,
	OnInit,
	ViewChild,
} from '@angular/core';
import { MatDrawer } from '@angular/material/sidenav';
import { BaseComponent } from '@core/base.component';
import { addressString } from '@core/util/address';
import { getMortgagePrimaryLender } from '@core/util/mortgage';
import { ILocalBranch } from '@models/localBranch';
import {
	Funding,
	FundingType,
	MatterMortgageWithAssociations,
} from '@models/mortgage';
import { FinancialInstitutionLender } from '@models/lender';
import { TrustAccount } from '@models/trustAccount';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select } from '@ngxs/store';
import { LocalBranchesState } from '@state/business/localBranches/localBranches.state';
import { TrustAccountsState } from '@state/business/trustAccounts/trustAccounts.state';
import { UserSelectors } from '@state/global/user';
import {
	MatterMortgageDispatch,
	MatterMortgagesState,
} from '@state/matter/matter_mortgages';
import { FundingDetailsOptions } from '@state/matter/matter_mortgages/contracts/funding-details-options';
import { FundingOptionsDetailsConfig } from '@state/matter/matter_mortgages/contracts/funding-options-details-config';
import { cloneDeep } from 'lodash';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { AddEditLocalBranchComponent } from 'src/app/private/manage/trust-accounts/add-edit-local-branch/add-edit-local-branch.component';
import {
	DropdownOption,
	DropdownResult,
} from 'src/app/shared/atomic/atoms/dropdown-options/dropdown-options.component';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

interface VM {
	fundingTypeOptions: DropdownOption<FundingType>[];
	bridgeSelected: boolean;
	selectedFundingTypeText: string;
	accountNumber: string;
	trustAccountOptions: DropdownOption<TrustAccount>[];
	localBranchOptions: DropdownOption<ILocalBranch>[];
	selectedTrustAccount: DropdownOption<TrustAccount>;
	selectedLocalBranch: DropdownOption<ILocalBranch>;
	isWireTransferOnly: boolean;
	isBranchPickup: boolean;
	hasBranchPickupOptional: boolean;
	hasBranchPickupRequired: boolean;
	hasBridgeFunding: boolean;
	isCourier: boolean;
	isWireTransfer: boolean;
	orientation: 'vertical' | 'horizontal';
	isFullyClosed: boolean;
}
const DefaultVM: VM = {
	fundingTypeOptions: [],
	bridgeSelected: false,
	selectedFundingTypeText: '',
	accountNumber: '',
	trustAccountOptions: [],
	localBranchOptions: [],
	selectedTrustAccount: null,
	selectedLocalBranch: null,
	isWireTransferOnly: false,
	isBranchPickup: false,
	hasBranchPickupOptional: false,
	hasBranchPickupRequired: false,
	hasBridgeFunding: false,
	isCourier: false,
	isWireTransfer: false,
	orientation: 'vertical',
	isFullyClosed: true,
};
enum DrawerType {
	TRUST_ACCOUNT = 'trustAccount',
	LOCAL_BRANCH = 'localBranch',
}
const FundingTypeLabels = {
	WIRE_TRANSFER: 'Wire Transfer/Electronic Deposit',
	BRANCH_PICKUP: 'Cheque (Branch Pickup)',
	NO_FUNDING_REQUIRED: 'No Funding Required',
	COURIER: 'Cheque Courier',
};
@UntilDestroy()
@Component({
	selector: 'app-closer-sync-funding-details',
	templateUrl: './closer-sync-funding-details.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class CloserSyncFundingDetailsComponent
	extends BaseComponent<VM>
	implements OnInit
{
	public _mortgage: MatterMortgageWithAssociations;
	@Select(TrustAccountsState.getAll) trustAccounts$: Observable<TrustAccount[]>;
	@Select(LocalBranchesState.getAll) localBranches$: Observable<ILocalBranch[]>;
	@Select(UserSelectors.getCurrentUserIsAdmin) admin$: Observable<boolean>;
	@Select(MatterMortgagesState.getSelectedMortgage)
	mortgage$: Observable<MatterMortgageWithAssociations>;
	@Select(MatterMortgagesState.fundingOptionsDetailsConfig)
	fundingOptionsDetailsConfig$: Observable<FundingOptionsDetailsConfig>;
	@ViewChild('drawer') drawer: MatDrawer;

	public fundingOptionLabels = FundingTypeLabels;

	public fundingTypeText = {
		[FundingType.WIRE_TRANSFER]: FundingTypeLabels.WIRE_TRANSFER,
		[FundingType.BRANCH_PICKUP]: FundingTypeLabels.BRANCH_PICKUP,
		[FundingType.NO_FUNDING_REQUIRED]: FundingTypeLabels.NO_FUNDING_REQUIRED,
		[FundingType.COURIER]: FundingTypeLabels.COURIER,
	};

	public bridgeOptions = [
		{ value: true, label: 'Yes' },
		{ value: false, label: 'No' },
	];

	public displayLocalBranch$: Observable<boolean>;

	@Input() set orientation(orientation: 'vertical' | 'horizontal') {
		this.VM = { orientation };
	}

	public financialInstitution: FinancialInstitutionLender;
	/**
	 * Check if the funding source is wire transfer only.
	 *
	 * @return {boolean} true if wire transfer is the only funding option, false otherwise
	 */
	isWireTransferOnly(_fundingDetailsOptions: FundingDetailsOptions): boolean {
		if (!_fundingDetailsOptions) {
			return false;
		}
		return (
			_fundingDetailsOptions?.wireTransfer &&
			!_fundingDetailsOptions?.branchPickup &&
			!_fundingDetailsOptions?.noFunding &&
			!_fundingDetailsOptions?.courier &&
			!_fundingDetailsOptions?.branchPickupRequired
		);
	}
	public id = null;
	public currentDrawer: DrawerType;
	public DrawerType = DrawerType;
	constructor(
		private mortgageDispatch: MatterMortgageDispatch,
		private drawerService: DrawerService,
	) {
		super(DefaultVM);
	}

	displayLocalBranch(): Observable<boolean> {
		return this.vm$.pipe(
			untilDestroyed(this),
			map(
				(vm) =>
					vm.isBranchPickup ||
					vm.hasBranchPickupRequired ||
					(vm.isWireTransfer && vm.hasBranchPickupOptional),
			),
		);
	}

	generateFundingTypeOptions(
		_fundingDetailsOptions: FundingDetailsOptions,
	): DropdownOption<FundingType>[] {
		return Object.entries(this.fundingTypeText).reduce(
			(acc, [value, name]: [FundingType, string]) => {
				if (
					(value === FundingType.WIRE_TRANSFER &&
						_fundingDetailsOptions?.wireTransfer) ||
					(value === FundingType.BRANCH_PICKUP &&
						_fundingDetailsOptions?.branchPickup) ||
					(value === FundingType.NO_FUNDING_REQUIRED &&
						_fundingDetailsOptions?.noFunding) ||
					(value === FundingType.COURIER && _fundingDetailsOptions?.courier)
				) {
					acc.push({ value, name });
				}
				return acc;
			},
			[] as DropdownOption<FundingType>[],
		);
	}

	ngOnInit(): void {
		this.displayLocalBranch$ = this.displayLocalBranch();
		combineLatest([
			this.mortgage$,
			this.localBranches$,
			this.trustAccounts$,
			this.fundingOptionsDetailsConfig$,
		])
			.pipe(untilDestroyed(this))
			.subscribe(
				([
					mortgage,
					localBranches,
					trustAccounts,
					fundingDetailOptionsConfig,
				]) => {
					if (
						!mortgage &&
						localBranches &&
						trustAccounts &&
						fundingDetailOptionsConfig
					) {
						return;
					}

					const newVM: VM = cloneDeep(this.VM);
					const fundingDetailsOptions = fundingDetailOptionsConfig?.options;
					this._mortgage = mortgage;
					this.financialInstitution = getMortgagePrimaryLender(mortgage);

					const isWireTransferOnlyEnabled = this.isWireTransferOnly(
						fundingDetailsOptions,
					);

					const fundingDetails = mortgage?.fundingDetails;
					const hasDefaultEnabled = fundingDetailsOptions?.default;

					if (isWireTransferOnlyEnabled && this?._mortgage) {
						if (!fundingDetails?.type) {
							this.mortgageDispatch.updateMortgage(this._mortgage.id, {
								fundingDetails: { type: FundingType.WIRE_TRANSFER } as Funding,
							});
						}
					}

					if (!fundingDetails?.type && hasDefaultEnabled) {
						this.mortgageDispatch.updateMortgage(this._mortgage.id, {
							fundingDetails: { type: FundingType.WIRE_TRANSFER } as Funding,
						});
					}

					newVM.accountNumber = fundingDetails?.accountNumber ?? '';
					newVM.fundingTypeOptions = this.generateFundingTypeOptions(
						fundingDetailsOptions,
					);
					newVM.selectedFundingTypeText =
						this.fundingTypeText[fundingDetails?.type] ?? '';

					newVM.bridgeSelected =
						fundingDetails?.bridgeFunding?.required ?? false;
					newVM.isCourier = fundingDetails?.type === FundingType.COURIER;
					newVM.isWireTransfer =
						fundingDetails?.type === FundingType.WIRE_TRANSFER;
					newVM.isBranchPickup =
						fundingDetails?.type === FundingType.BRANCH_PICKUP;
					newVM.hasBridgeFunding = fundingDetailsOptions?.bridgeFunding;
					newVM.hasBranchPickupOptional =
						fundingDetailsOptions?.branchPickupOptional;
					newVM.hasBranchPickupRequired =
						fundingDetailsOptions?.branchPickupRequired;
					newVM.isWireTransferOnly = isWireTransferOnlyEnabled;

					if (localBranches) {
						newVM.localBranchOptions = localBranches.map((branch) => ({
							value: branch,
							name: `${branch.shortName} - ${branch.transitNumber} - ${addressString(branch.address)}`,
						})) as DropdownOption<ILocalBranch>[];
						newVM.localBranchOptions = [...newVM.localBranchOptions];

						newVM.localBranchOptions = localBranches
							.filter(
								(branch) =>
									branch &&
									branch.institutionGlobalReferenceId ===
										fundingDetailOptionsConfig?.globalReferenceId,
							)
							.map((branch) => ({
								value: branch,
								name: `${branch.shortName} - ${branch.transitNumber} - ${addressString(branch.address)}`,
							})) as DropdownOption<ILocalBranch>[];

						const localBranch = this._mortgage?.fundingDetails?.localBranch;

						if (localBranch) {
							newVM.selectedLocalBranch = newVM.localBranchOptions.find(
								(option) =>
									option?.value?.id === localBranch.id ||
									option?.value?.transitNumber === localBranch.transitNumber,
							);
						}
					}

					const allTrustAccounts = [
						this._mortgage?.fundingDetails?.trustAccountOnInstructions,
						...trustAccounts,
					];

					if (allTrustAccounts) {
						newVM.trustAccountOptions = allTrustAccounts
							.filter(
								(account: TrustAccount) =>
									account !== null && account !== undefined,
							)
							.map<
								DropdownOption<TrustAccount>
							>((account: TrustAccount) => this.mapToDropdownOption(account));
						let trustAccountId =
							this._mortgage?.fundingDetails?.trustAccount?.id;
						if (this._mortgage?.fundingDetails?.useTrustAccountOnInstructions)
							trustAccountId =
								this._mortgage?.fundingDetails?.trustAccountOnInstructions?.id;
						if (trustAccountId)
							newVM.selectedTrustAccount = newVM.trustAccountOptions.find(
								(option) => option?.value?.id === trustAccountId,
							);
					}

					this.VM = newVM;
				},
			);
	}

	onTrustAccountCreated(trustAccount: TrustAccount) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: { trustAccount } as Funding,
		});
	}

	mapToDropdownOption(account: TrustAccount): DropdownOption<TrustAccount> {
		return {
			value: account,
			name: this.constructAccountName(account),
		};
	}
	constructAccountName(account: TrustAccount): string {
		if (
			account?.id ===
			this._mortgage?.fundingDetails?.trustAccountOnInstructions?.id
		)
			return `${account?.institutionName} - ****${account?.last3DigitsAccountNumber} (from instructions)`;
		return `${account?.institutionShortName ?? ''} - ${account?.nickname} - ****${account?.last3DigitsAccountNumber ?? ''}`;
	}
	onSelectFundingType(
		event: DropdownResult<DropdownOption<FundingType>>,
	): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: { type: event?.result?.value } as Funding,
		});
	}
	onSelectTrustAccount(
		event: DropdownResult<DropdownOption<TrustAccount>>,
	): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: {
				trustAccount: event?.result?.value,
				useTrustAccountOnInstructions:
					event?.result?.value?.id ===
					this._mortgage?.fundingDetails?.trustAccountOnInstructions?.id,
			} as Funding,
		});
	}
	onSelectLocalBranch(
		event: DropdownResult<DropdownOption<ILocalBranch>>,
	): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: { localBranch: event?.result?.value } as Funding,
		});
	}
	removeLocalBranch(): void {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: { localBranch: null } as Funding,
		});
	}
	onAccountNumberChanged = (event) => {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: { accountNumber: event } as Funding,
		});
	};

	onBridgeOptionChanged($event) {
		this.mortgageDispatch.updateMortgage(this._mortgage.id, {
			fundingDetails: { bridgeFunding: { required: $event } } as Funding,
		});
	}
	edit(drawerType: DrawerType, id: string) {
		this.currentDrawer = drawerType;
		this.id = id;
		if (drawerType === DrawerType.LOCAL_BRANCH) {
			this.drawerService.openDrawer(AddEditLocalBranchComponent, {
				title: 'Add Local Branch',
				size: 'sm',
				data: {
					financialInstitution: this.financialInstitution,
					mortgageId: this._mortgage.id,
				},
			});
		} else {
			this.openDrawer();
		}
	}
	closeDrawer() {
		this.drawer.close();
		this.id = null;
		this.markAsFullyClosedAfter(300);
	}
	markAsFullyClosedAfter(ms) {
		setTimeout(() => {
			this.VM = {
				isFullyClosed: true,
			};
		}, ms);
	}
	openDrawer() {
		this.drawer.open();
		this.VM = {
			isFullyClosed: false,
		};
	}
	create(drawerType: DrawerType) {
		this.edit(drawerType, null);
	}
}
